const getPlatformClass = () => {
    const useragent = navigator.userAgent.toLowerCase();
    const isMac = useragent.includes('mac');
    const isWindows = useragent.includes('win');
    const isChrome = useragent.includes('chrome');
    const isFF = useragent.includes('firefox');
    if (isWindows && isChrome)
        return 'win-chrome';
    if (isWindows && isFF)
        return 'win-ff';
    if (isMac && isFF)
        return 'mac-ff';
    return 'unknown';
};
let __idCounter = 0;
const generateUniqueId = (prefix = '') => {
    try {
        const winCrypto = globalThis.crypto;
        if (winCrypto && typeof winCrypto.randomUUID === 'function') {
            return prefix + winCrypto.randomUUID();
        }
        if (winCrypto && typeof winCrypto.getRandomValues === 'function') {
            const arr = new Uint32Array(3);
            winCrypto.getRandomValues(arr);
            return (prefix +
                Array.from(arr)
                    .map(n => n.toString(36))
                    .join(''));
        }
    }
    catch (e) {
        // If crypto APIs throw for any reason, log the error and continue with fallback.
        // Logging makes the failure visible to operators and satisfies static analysis rules
        // that require exceptions to be handled.
        // eslint-disable-next-line no-console
        console.warn('generateUniqueId: secure crypto unavailable, using fallback id generation', e);
    }
    __idCounter += 1;
    return prefix + Date.now().toString(36) + '-' + __idCounter.toString(36);
};
// RTL value can be overridden by web component's 'rtl' @property for compatibility with Storybook
// The html root "dir" attribute is used for the Episerver and "rtl" attribute should not be used in Razor views
const isRTL = () => { var _a; return ((_a = document.querySelector('html')) === null || _a === void 0 ? void 0 : _a.getAttribute('dir')) === 'rtl'; };
const getLang = () => { var _a; return (_a = document.querySelector('html')) === null || _a === void 0 ? void 0 : _a.getAttribute('lang'); };
const isSafari = /Version\/[\d.]+.*Safari/.test(window.navigator.userAgent);
const isLink = (element, contextToCheck = null) => {
    var _a;
    const parentLinkElement = element === null || element === void 0 ? void 0 : element.closest('a');
    const childAnchor = (_a = element === null || element === void 0 ? void 0 : element.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(':scope > a');
    return (element instanceof HTMLAnchorElement ||
        parentLinkElement ||
        (childAnchor && childAnchor instanceof HTMLAnchorElement) ||
        (contextToCheck &&
            contextToCheck.forEach((context) => (element === null || element === void 0 ? void 0 : element.parentNode) instanceof context)));
};
const getLinkURL = (element) => {
    var _a;
    const parent = element === null || element === void 0 ? void 0 : element.closest('a');
    const childAnchor = (_a = element === null || element === void 0 ? void 0 : element.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(':scope > a');
    let url = '';
    if (element instanceof HTMLAnchorElement) {
        url = element.href;
    }
    else if (parent) {
        url = parent.href;
    }
    else if (childAnchor && childAnchor instanceof HTMLAnchorElement) {
        url = childAnchor.href;
    }
    return url;
};
function observeDOM(obj, callback) {
    const { MutationObserver } = window;
    if (!obj || obj.nodeType !== 1)
        return;
    const mutationObserver = new MutationObserver(callback);
    mutationObserver.observe(obj, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'className'],
    });
    // eslint-disable-next-line consistent-return
    return mutationObserver;
}
const getSlotContentByName = (element, slotName) => {
    var _a;
    const slot = (_a = element.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(`slot[name="${slotName}"]`);
    if (!slot) {
        console.warn(`Could not find text content in slot[name="${slotName}"] or parent element/slot does not exist`);
        return null;
    }
    return slot === null || slot === void 0 ? void 0 : slot.assignedElements()[0];
};
const setDataAttributes = (ctx, headingSlotName, textSlotName) => {
    if (headingSlotName !== null)
        ctx.headingSlot = getSlotContentByName(ctx, headingSlotName);
    if (ctx.headingSlot)
        ctx.headingSlot.dataset.text = ctx.headingSlot.innerHTML;
    if (textSlotName !== null)
        ctx.textSlot = getSlotContentByName(ctx, textSlotName);
    if (ctx.textSlot)
        ctx.textSlot.dataset.text = ctx.textSlot.innerHTML;
};
const getContainerClass = (isIcon, size) => {
    if (isIcon) {
        return size === 'Small' ? 'small-icon-container' : 'large-icon-container';
    }
    return size === 'Small' ? 'small-image-container' : 'large-image-container';
};
const getEllipsis = (text) => text.length >= 150 ? `${text.substring(0, 150)}...` : text;
/**
 * Processes CMS rich-text HTML containing <table> elements:
 * - Strips all TinyMCE inline styles / legacy HTML attributes
 * - Promotes the first <tbody> row to <thead> and normalizes its cells as <th scope="col">
 * - Wraps each table in <div class="table-wrapper">
 * - Leaves table header coloring to shared CSS defaults (fixed white headers)
 *
 * Used by every component that renders unsafeHTML rich text with the shared rich-text-shadow styles.
 */
const stripLegacyAttributes = (elements) => {
    elements.forEach(el => {
        el.removeAttribute('style');
        el.removeAttribute('bgcolor');
        el.removeAttribute('bordercolor');
        el.removeAttribute('width');
        el.removeAttribute('height');
        el.removeAttribute('valign');
        el.removeAttribute('align');
    });
};
const convertCellToTh = (cell, docRef, scope) => {
    const th = docRef.createElement('th');
    if (scope === 'row') {
        // copy attributes for row headers
        Array.from(cell.attributes).forEach(attr => th.setAttribute(attr.name, attr.value));
    }
    th.setAttribute('scope', scope);
    th.innerHTML = cell.innerHTML;
    cell.replaceWith(th);
};
const promoteFirstRowToThead = (table, docRef) => {
    const tbody = table.querySelector('tbody');
    const firstRow = tbody === null || tbody === void 0 ? void 0 : tbody.querySelector('tr');
    if (firstRow && tbody) {
        Array.from(firstRow.querySelectorAll('td')).forEach(td => convertCellToTh(td, docRef, 'col'));
        const thead = docRef.createElement('thead');
        thead.appendChild(firstRow);
        table.insertBefore(thead, tbody);
    }
};
const ensureTheadScopes = (table, docRef) => {
    table.querySelectorAll('thead td').forEach(td => convertCellToTh(td, docRef, 'col'));
    table.querySelectorAll('thead th:not([scope])').forEach(th => th.setAttribute('scope', 'col'));
};
const ensureFirstColumnRowHeaders = (table) => {
    table.querySelectorAll('tbody').forEach(tbody => {
        Array.from(tbody.querySelectorAll('tr')).forEach(row => {
            const firstCell = row.querySelector('th, td');
            if (!firstCell)
                return;
            const tag = firstCell.tagName.toLowerCase();
            if (tag === 'th' && !firstCell.hasAttribute('scope')) {
                firstCell.setAttribute('scope', 'row');
            }
        });
    });
};
const markRowHeaderState = (table) => {
    const hasRowHeaders = table.querySelector('tbody th[scope="row"]') !== null;
    table.classList.toggle('has-row-header-column', hasRowHeaders);
};
const trimLeadingWhitespaceInNode = (node) => {
    var _a;
    const child = node.firstChild;
    if (!child) {
        return false;
    }
    if (child.nodeType === Node.TEXT_NODE) {
        const original = (_a = child.textContent) !== null && _a !== void 0 ? _a : '';
        const trimmed = original.replace(/^[\s\u00A0]+/, '');
        if (trimmed.length === 0) {
            child.remove();
            trimLeadingWhitespaceInNode(node);
            return true;
        }
        if (trimmed !== original) {
            child.textContent = trimmed;
            return true;
        }
        return false;
    }
    if (child.nodeType === Node.ELEMENT_NODE) {
        return trimLeadingWhitespaceInNode(child);
    }
    return false;
};
const normalizeCheckmarkListLeadingWhitespace = (docRef) => {
    let changed = false;
    Array.from(docRef.querySelectorAll('ul.checkmarks li')).forEach(li => {
        if (trimLeadingWhitespaceInNode(li)) {
            changed = true;
        }
    });
    return changed;
};
function wrapRichTextTables(content) {
    if (!content)
        return content;
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    // Ensure footnote paragraphs are announced semantically to assistive tech.
    // The CMS outputs <p class="footnote"> for styling only — add role="note".
    Array.from(doc.querySelectorAll('p.footnote')).forEach(p => {
        if (!p.hasAttribute('role'))
            p.setAttribute('role', 'note');
    });
    // TinyMCE can prepend spaces/&nbsp; inside checkmark list items.
    // Remove that leading whitespace to keep marker alignment consistent.
    const checkmarkWhitespaceNormalized = normalizeCheckmarkListLeadingWhitespace(doc);
    const tables = Array.from(doc.querySelectorAll('table'));
    const h1s = Array.from(doc.querySelectorAll('h1'));
    // If there are no tables and no top-level pasted <h1> headings, nothing to do.
    if (!tables.length && !h1s.length && !checkmarkWhitespaceNormalized) {
        return doc.body.innerHTML;
    }
    // Downgrade any <h1> elements produced by pasted content to <h2>
    // Editors are only allowed to create headings starting at h2 (see TinyMCE BlockFormats).
    // This prevents imported h1s from breaking page-level heading hierarchy (WCAG 1.3.1).
    if (h1s.length) {
        h1s.forEach(h1 => {
            const h2 = doc.createElement('h2');
            h2.innerHTML = h1.innerHTML;
            h1.replaceWith(h2);
        });
    }
    tables.forEach(table => {
        var _a;
        // Strip table-level legacy attributes
        table.removeAttribute('style');
        table.removeAttribute('border');
        table.removeAttribute('bordercolor');
        table.removeAttribute('bgcolor');
        table.removeAttribute('frame');
        table.removeAttribute('rules');
        table.removeAttribute('cellpadding');
        table.removeAttribute('cellspacing');
        // Strip legacy attributes from child elements
        stripLegacyAttributes(table.querySelectorAll('td, th, tr, thead, tbody, tfoot, colgroup, col'));
        if (!table.querySelector('thead')) {
            promoteFirstRowToThead(table, doc);
        }
        ensureTheadScopes(table, doc);
        ensureFirstColumnRowHeaders(table);
        markRowHeaderState(table);
        // Wrap in .table-wrapper (guard against double-wrapping)
        if (!((_a = table.parentElement) === null || _a === void 0 ? void 0 : _a.classList.contains('table-wrapper'))) {
            const wrapper = doc.createElement('div');
            wrapper.className = 'table-wrapper';
            // Only promote to a labeled landmark when the table has a <caption>.
            // role="region" without an accessible name is an ARIA violation (WCAG 1.3.1)
            // and confuses screen readers — omit both attributes when no caption exists.
            const caption = table.querySelector('caption');
            let captionId = (caption === null || caption === void 0 ? void 0 : caption.getAttribute('id')) || '';
            if (caption && !captionId) {
                captionId = generateUniqueId('rte-caption-');
                caption.setAttribute('id', captionId);
            }
            if (captionId) {
                wrapper.setAttribute('tabindex', '0');
                wrapper.setAttribute('role', 'region');
                wrapper.setAttribute('aria-labelledby', captionId);
            }
            table.replaceWith(wrapper);
            wrapper.appendChild(table);
        }
    });
    return doc.body.innerHTML;
}

export { generateUniqueId, getContainerClass, getEllipsis, getLang, getLinkURL, getPlatformClass, getSlotContentByName, isLink, isRTL, isSafari, observeDOM, setDataAttributes, wrapRichTextTables };
